import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../api/api-context';
import type { ReviewQueueMode, ReviewSummary } from '../api/types';
import { SESSION_LIMITS, parseScope, relativeDue, type SessionStats } from '../state/review-session';
import { ReviewTrainer } from './ReviewTrainer';

const MODE_TITLES: Record<ReviewQueueMode, string> = {
  due: 'Reviewing due moves',
  new: 'Learning new moves',
  all: 'Practicing every move',
};

function isMode(value: string | null): value is ReviewQueueMode {
  return value === 'due' || value === 'new' || value === 'all';
}

function Forecast({ summary }: { summary: ReviewSummary }): JSX.Element {
  const peak = Math.max(1, ...summary.forecast.map((day) => day.count));
  return (
    <figure className="review-forecast">
      <figcaption>Due over the next 7 days</figcaption>
      <ol>
        {summary.forecast.map((day, index) => {
          const label = index === 0 ? 'Today' : new Date(`${day.date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
          return (
            <li key={day.date} aria-label={`${label}: ${day.count} due`}>
              <span className="forecast-bar" style={{ height: `${(day.count / peak) * 100}%` }} />
              <span className="forecast-count">{day.count}</span>
              <span className="forecast-day">{label}</span>
            </li>
          );
        })}
      </ol>
    </figure>
  );
}

/** Practice hub, session, and results: /repertoires/:id/review?mode=&from= */
export function ReviewPage(): JSX.Element {
  const { repertoireId = '' } = useParams<{ repertoireId: string }>();
  const [params, setParams] = useSearchParams();
  const api = useApi();
  const queryClient = useQueryClient();
  const scope = parseScope(params.get('from'));
  const modeParam = params.get('mode');
  const mode = isMode(modeParam) ? modeParam : null;
  const [session, setSession] = useState(0);
  const [result, setResult] = useState<{ stats: SessionStats; reviewed: number } | null>(null);

  const repertoire = useQuery({
    queryKey: ['repertoire', repertoireId],
    queryFn: () => api.getRepertoire(repertoireId),
    enabled: repertoireId !== '',
  });
  const summary = useQuery({
    queryKey: ['review-summary', repertoireId, scope?.zobristHash, scope?.positionId],
    queryFn: () => api.getReviewSummary(repertoireId, scope),
    enabled: repertoireId !== '' && (mode === null || result !== null),
  });
  const queue = useQuery({
    queryKey: ['review-queue', repertoireId, mode, scope?.zobristHash, scope?.positionId, session],
    queryFn: () => api.getReviewQueue(repertoireId, mode!, { limit: SESSION_LIMITS[mode!], scope }),
    enabled: repertoireId !== '' && mode !== null && result === null,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  function go(nextMode: ReviewQueueMode | null): void {
    const next = new URLSearchParams(params);
    if (nextMode) next.set('mode', nextMode);
    else next.delete('mode');
    setResult(null);
    setSession((value) => value + 1);
    setParams(next);
  }

  function finish(stats: SessionStats, reviewed: number): void {
    setResult({ stats, reviewed });
    void queryClient.invalidateQueries({ queryKey: ['review-summary', repertoireId] });
  }

  const name = repertoire.data?.data.name ?? 'Repertoire';
  const side = repertoire.data?.data.side ?? 'white';
  const counts = summary.data;

  const header = (
    <>
      <Link to={`/repertoires/${repertoireId}`} className="muted">← {name}</Link>
      <p className="studio-eyebrow">SPACED REPETITION</p>
      <h1>{mode && result === null ? MODE_TITLES[mode] : 'Practice'}</h1>
      {scope && (
        <p className="review-scope">
          Only moves below the position you chose.{' '}
          <button type="button" className="link-button" onClick={() => {
            const next = new URLSearchParams(params);
            next.delete('from');
            setParams(next);
          }}>Practice the whole repertoire</button>
        </p>
      )}
    </>
  );

  if (mode !== null && result === null) {
    return (
      <main className="page review-page">
        {header}
        {(queue.isPending || repertoire.isPending) && <p role="status">Loading your moves…</p>}
        {queue.isError && <p role="alert">Could not load the review queue. <button onClick={() => void queue.refetch()}>Try again</button></p>}
        {queue.data && repertoire.data && (queue.data.items.length === 0 ? (
          <section className="panel">
            <p role="status">
              {mode === 'due' ? 'Nothing is due right now.' : mode === 'new' ? 'There are no new moves to learn.' : 'There are no moves of yours to practice here yet.'}
            </p>
            <div className="review-actions">
              {mode !== 'new' && queue.data.summary.newCount > 0 && <button type="button" onClick={() => go('new')}>Learn {queue.data.summary.newCount} new</button>}
              {mode !== 'all' && queue.data.summary.totalCount > 0 && <button type="button" className="secondary" onClick={() => go('all')}>Practice all</button>}
              <button type="button" className="secondary" onClick={() => go(null)}>Back to practice</button>
            </div>
          </section>
        ) : (
          <ReviewTrainer
            key={session}
            repertoireId={repertoireId}
            side={side}
            items={queue.data.items}
            onDone={finish}
            onExit={() => go(null)}
          />
        ))}
      </main>
    );
  }

  if (result !== null) {
    const { stats, reviewed } = result;
    const graded = stats.correct + stats.alternative + stats.incorrect;
    const accuracy = graded ? Math.round(((stats.correct + stats.alternative) / graded) * 100) : 0;
    return (
      <main className="page review-page">
        {header}
        <section className="panel review-results" aria-labelledby="results-title">
          <h2 id="results-title">Session complete</h2>
          <dl className="review-stats">
            <div><dt>Moves</dt><dd>{reviewed}</dd></div>
            <div><dt>First try</dt><dd>{accuracy}%</dd></div>
            <div><dt>Missed</dt><dd>{stats.incorrect}</dd></div>
            {stats.skipped > 0 && <div><dt>Skipped</dt><dd>{stats.skipped}</dd></div>}
          </dl>
          {counts && (
            <p role="status">
              {counts.dueCount > 0
                ? `${counts.dueCount} more ${counts.dueCount === 1 ? 'move is' : 'moves are'} due now.`
                : counts.nextDueAt
                  ? `Next review ${relativeDue(counts.nextDueAt)}.`
                  : 'Nothing else is scheduled.'}
            </p>
          )}
          <div className="review-actions">
            {counts && counts.dueCount > 0 && <button type="button" className="studio-primary" onClick={() => go('due')}>Review {counts.dueCount} due</button>}
            {counts && counts.newCount > 0 && <button type="button" onClick={() => go('new')}>Learn {Math.min(counts.newCount, SESSION_LIMITS.new)} new</button>}
            <button type="button" className="secondary" onClick={() => go(null)}>Back to practice</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="page review-page">
      {header}
      <p className="muted">
        ItsAllPrep plays each line from the start and stops at your moves. Play the move from your
        repertoire; a mistake shows the right move, and it comes back at the end of the session.
        New moves are shown with an arrow first, then tested without one before the session moves
        on. Moves you know well are spaced further apart.
      </p>
      {summary.isPending && <p role="status">Loading your schedule…</p>}
      {summary.isError && <p role="alert">Could not load your review schedule. <button onClick={() => void summary.refetch()}>Try again</button></p>}
      {counts && (
        <>
          <dl className="review-stats" aria-label="Review schedule">
            <div className={counts.dueCount > 0 ? 'stat-due' : undefined}><dt>Due now</dt><dd>{counts.dueCount}</dd></div>
            <div><dt>New</dt><dd>{counts.newCount}</dd></div>
            <div><dt>Learned</dt><dd>{counts.scheduledCount + counts.dueCount}</dd></div>
            <div><dt>Your moves</dt><dd>{counts.totalCount}</dd></div>
          </dl>
          <div className="review-actions">
            <button type="button" className="studio-primary" disabled={counts.dueCount === 0} onClick={() => go('due')}>
              {counts.dueCount > 0 ? `Review ${counts.dueCount} due` : 'Nothing due'}
            </button>
            <button type="button" disabled={counts.newCount === 0} onClick={() => go('new')}>
              Learn {Math.min(counts.newCount, SESSION_LIMITS.new)} new
            </button>
            <button type="button" className="secondary" disabled={counts.totalCount === 0} onClick={() => go('all')}>
              Practice all
            </button>
          </div>
          {counts.dueCount === 0 && counts.nextDueAt && (
            <p className="hint">Next review {relativeDue(counts.nextDueAt)}.</p>
          )}
          {counts.totalCount === 0 && (
            <p>Add your moves in the <Link to={`/repertoires/${repertoireId}`}>line builder</Link> to start practicing.</p>
          )}
          {counts.totalCount > 0 && <Forecast summary={counts} />}
        </>
      )}
    </main>
  );
}
